/* eslint-disable spire/export-styles */
import { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer from "@insite/mobius/GridContainer/GridContainer";
import GridItem from "@insite/mobius/GridItem";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Select from "@insite/mobius/Select/Select";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import loadShipViaAccessorials from "@tKgpco/Handlers/Accessorials/LoadShipViaAccessorials";
import { getWORData } from "@tKgpco/Services/WORService";
import CustomApplicationState from "@tKgpco/Store/CustomApplicationState";
import { ShipVia, WORCarrier } from "@tKgpco/Store/Data/WOR/WORCarrierState";
import { TYPOGRAPHY } from "@tKgpco/theme/design";
import { useWORContext } from "@tKgpco/Widgets/WOR/WORCheckoutReviewSubmitPage";
import * as React from "react";
import { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomApplicationState) => {
    return {
        isLoadingCarriers: state.KGPState.worCarriers.isLoading,
        carriers: state.KGPState.worCarriers.carriers,
    };
};

const mapDispatchToProps = {
    loadShipViaAccessorials,
};

interface WorCheckoutCarrierStyles {
    warningSectionCustom?: InjectableCss;
    warningIconCustom?: IconPresentationProps;
    warningTextCustom?: InjectableCss;
}
const worCheckoutCarrierStyles: WorCheckoutCarrierStyles = {
    warningSectionCustom: {
        css: css`
            display: flex;
            align-items: flex-start;
            border: 2px solid #f0b003;
            padding: 10px 10px;
            width: 100%;
            margin: 15px;
            .errorList {
                list-style-type: disc;
                margin-left: 1rem;
                li {
                    color: #f0b003;
                }
            }
        `,
    },
    warningTextCustom: {
        css: css`
            font-size: 15px;
            color: #f0b003;
            font-weight: ${TYPOGRAPHY.weight.bold};
        `,
    },
    warningIconCustom: {
        src: "AlertCircle",
        color: "#f0b003",
        css: css`
            margin-right: 10px;
        `,
    },
};

const styles = worCheckoutCarrierStyles;
const StyledSection = getStyledWrapper("section");
type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const WORChekoutReviewAndSubmitCarriers: React.FunctionComponent<Props> = ({
    isLoadingCarriers,
    carriers,
    loadShipViaAccessorials,
    fields,
}: Props) => {
    const [selectedCarrier, setSelectedCarrier] = useState<WORCarrier | undefined>();
    const [shipVias, setShipVias] = useState<ShipVia[] | undefined>();
    const [selectedShipVia, setSelectedShipVia] = useState<ShipVia | undefined>();
    const { worData, worDispatch } = useWORContext();
    const worPageData = getWORData();
    useEffect(() => {
        worDispatch({ type: "SET_DATA", payload: { selectedCarrier, selectedShipVia } });
        if (selectedShipVia?.id && selectedCarrier?.id) {
            loadShipViaAccessorials({
                shipViaId: selectedShipVia?.id || "",
                carrierId: selectedCarrier?.id || "",
            });
        }
    }, [selectedCarrier, selectedShipVia]);

    if (isLoadingCarriers) {
        return null;
    }

    const handleChangeCarrier = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const pCarrier = carriers.find(carrier => carrier.carrierName === e.target.value);
        setSelectedCarrier(pCarrier);
        const pShipVias = pCarrier?.shipVias;
        setShipVias(pShipVias);
        const defaultShipVia = pShipVias?.find(sVia => sVia.isDefault);
        setSelectedShipVia(defaultShipVia);
    };

    const handleChangeShipVia = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const defaultShipVia = shipVias?.find(sVia => sVia.serviceCode === e.target.value);
        setSelectedShipVia(defaultShipVia);
    };
    const isOverLengthProduct = worPageData.items.filter((item: any) => item?.isOverlength?.toLowerCase() === "true");

    return (
        <GridContainer
            offsetCss={css`
                max-width: 100% !important;
            `}
        >
            {isOverLengthProduct.length > 0 && (
                <StyledSection {...styles.warningSectionCustom}>
                    <Icon {...styles.warningIconCustom}></Icon>
                    <Typography {...styles.warningTextCustom}>{siteMessage("overlength-wor-message")}</Typography>
                </StyledSection>
            )}
            <GridItem width={[12, 12, 6, 6, 6]}>
                {carriers && carriers.length > 0 ? (
                    <Select
                        value={selectedCarrier?.carrierName || ""}
                        onChange={handleChangeCarrier}
                        label="Select Carrier"
                        labelProps={{ size: "14px" }}
                        required
                    >
                        <option value="">Select Carrier</option>
                        {carriers
                            .filter(carrier => {
                                if (carrier.carrierName === "Expedited" && isOverLengthProduct.length > 0) {
                                    return false;
                                }
                                return true;
                            })
                            .map(carrier => (
                                <option key={carrier.carrierName} value={carrier.carrierName}>
                                    {carrier.carrierName}
                                </option>
                            ))}
                    </Select>
                ) : null}
            </GridItem>
            <GridItem width={[12, 12, 6, 6, 6]}>
                <Select
                    value={selectedShipVia?.serviceCode || ""}
                    onChange={handleChangeShipVia}
                    label="Select Service"
                    labelProps={{ size: "14px" }}
                    required
                >
                    <option value="">Select ShipVia</option>
                    {shipVias &&
                        shipVias.length > 0 &&
                        shipVias.map(pShipVia => (
                            <option key={pShipVia?.serviceCode} value={pShipVia?.serviceCode}>
                                {pShipVia?.serviceName}
                            </option>
                        ))}
                </Select>
            </GridItem>
        </GridContainer>
    );
};
const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(WORChekoutReviewAndSubmitCarriers),
    definition: {
        displayName: "WOR Checkout Carriers",
        group: "Checkout - Review & Submit",
    },
};
export default widgetModule;
